import { createContext, use, useMemo, useState } from 'react'

/**
 * A runtime-error location to surface in the Monaco editor.
 * Published by the Test Results pane's error panel (FailurePanel), consumed
 * by useMonacoEditor, which renders it as a red gutter marker + squiggle and
 * clears it on the user's next edit.
 */
export interface EditorErrorMarker {
  /** 1-based line in the user's editor (the innermost user frame). */
  line: number
  /** Hover text, e.g. "NameError: name 'num' is not defined". */
  message: string
}

interface ErrorMarkerState {
  marker: EditorErrorMarker | null
  setMarker: (marker: EditorErrorMarker | null) => void
}

const ErrorMarkerContext = createContext<ErrorMarkerState | null>(null)

export function ErrorMarkerProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [marker, setMarker] = useState<EditorErrorMarker | null>(null)

  const value = useMemo<ErrorMarkerState>(
    () => ({ marker, setMarker }),
    [marker],
  )

  return <ErrorMarkerContext value={value}>{children}</ErrorMarkerContext>
}

export function useErrorMarkerOptional(): ErrorMarkerState | null {
  return use(ErrorMarkerContext)
}
